import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { WatchlistInfo, Watchlist } from "@foundation/watchlist-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { updateWatchlistUserUDST } from "~api/portfolio/fetchWatchlistUDST";
import { DashboardViewType, PortfolioForm } from "~types/portfolio";
import { SHOW_CHARTS_INITIAL_STATE_STORAGE_KEY } from "~constants/Portfolio";

const CLOSE_ANIMATION_DELAY = 400;

export type PortfolioType = "NEW" | "EMPTY" | "SAVED";

export type PortfolioPageContextType = {
  showCharts: boolean;
  portfolioType?: PortfolioType;
  renderingCharts: boolean;
  toggleCharts: () => void;
  portfolios: Array<Watchlist>;
  portfolioForm?: PortfolioForm;
  portfolioInfos: Array<WatchlistInfo>;
  selectedPortfolio?: Watchlist;
  selectedPortInfo?: WatchlistInfo;
  dashboardView: DashboardViewType;
  removePortfolio: (portfolioId: number) => void;
  upsertPortfolio: (newData: Watchlist) => void;
  updateSelectedPortInfo: (
    newData: WatchlistInfo | undefined,
    newDataType?: PortfolioType
  ) => void;
  updateSelectedPortfolio: (newData: Watchlist | undefined) => void;
  updatePortfolioForm: (newData: PortfolioForm | undefined) => void;
  updatePortfolioInfos: (newData: Array<WatchlistInfo>) => void;
  updateDashboardView: (newData: DashboardViewType) => void;
};

export const PortfolioPageContext = createContext<
  PortfolioPageContextType | undefined
>(undefined);

export const usePortfolioPage = () => {
  const context = useContext(PortfolioPageContext);

  if (!context)
    throw new Error(
      "usePortfolioPageData must be used within a PortfolioPageProvider"
    );

  return context;
};

interface PortfolioPageProviderProps {
  children: React.ReactNode;
}

export const PortfolioPageProvider: React.FC<PortfolioPageProviderProps> = ({
  children,
}) => {
  const [showCharts, setShowCharts] = useState(true);
  const [renderingCharts, setRenderingCharts] = useState(showCharts);
  const [portfolioType, setPortfolioType] = useState<PortfolioType | undefined>(
    undefined
  );

  const chartTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toggleCharts = useCallback(() => {
    if (chartTimeout.current) {
      clearTimeout(chartTimeout.current);
    }

    const nextShowCharts = !showCharts;

    const trackingEventName = nextShowCharts
      ? "watchlistShow"
      : "watchlistHide";
    const trackingEventLabel = nextShowCharts ? "show_charts" : "hide_charts";

    trackClickEvent(
      trackingEventName,
      trackingEventLabel,
      "click",
      "watchlist"
    );

    setShowCharts(nextShowCharts);

    if (nextShowCharts) {
      setRenderingCharts(nextShowCharts);

      return;
    }

    chartTimeout.current = setTimeout(() => {
      setRenderingCharts(nextShowCharts);
      chartTimeout.current = null;
    }, CLOSE_ANIMATION_DELAY);
  }, [showCharts]);

  useEffect(() => {
    localStorage.setItem(
      SHOW_CHARTS_INITIAL_STATE_STORAGE_KEY,
      `${showCharts}`
    );
  }, [showCharts]);

  useEffect(() => {
    setShowCharts(
      localStorage.getItem(SHOW_CHARTS_INITIAL_STATE_STORAGE_KEY) !== "false"
    );
  }, []);

  const [selectedPortInfo, setSelectedPortInfo] = useState<
    WatchlistInfo | undefined
  >(undefined);

  const [portfolios, setPortfolios] = useState<Array<Watchlist>>([]);
  const [dashboardView, setDashboardView] = useState<DashboardViewType>("DASH");
  const [portfolioInfos, setPortfolioInfos] = useState<Array<WatchlistInfo>>(
    []
  );

  const [isWatchlistUserUDST, setIsWatchlistUserUDST] =
    useState<boolean>(false);

  const [selectedPortfolio, setSelectedPortfolio] = useState<
    Watchlist | undefined
  >();

  const [portfolioForm, setPortfolioForm] = useState<
    PortfolioForm | undefined
  >();

  const updateSelectedPortInfo = useCallback(
    (newData: WatchlistInfo | undefined, newDataType?: PortfolioType) => {
      // store type of watchlist user selected
      setPortfolioType(newDataType);

      if (!newData) {
        setSelectedPortInfo(undefined);

        return;
      }

      setSelectedPortInfo({ ...newData });
    },
    []
  );

  const updateSelectedPortfolio = useCallback(
    (newData: Watchlist | undefined) => {
      if (!newData) {
        setSelectedPortfolio(undefined);

        return;
      }

      setSelectedPortfolio({ ...newData });
    },
    []
  );

  const upsertPortfolio = useCallback(
    (nextPortfolio: Watchlist) => {
      // update selected tab before updating all tabs
      updateSelectedPortInfo(nextPortfolio.portInfo);

      setPortfolioInfos((prevState) => {
        if (!prevState.length) {
          return [nextPortfolio.portInfo];
        }

        const portInfoIndex = prevState.findIndex(
          (portInfo) => portInfo.id === nextPortfolio.portInfo.id
        );

        if (portInfoIndex === -1) {
          return [...prevState, nextPortfolio.portInfo];
        }

        return prevState.map((item) =>
          item.id === nextPortfolio.portInfo.id
            ? { ...nextPortfolio.portInfo }
            : item
        );
      });

      // update current portfolio
      setSelectedPortfolio(nextPortfolio);

      // update portfolio when available in list or add a new entry
      setPortfolios((prevPortfolios) => {
        if (!prevPortfolios.length) {
          return [nextPortfolio];
        }

        const portfolioIndex = prevPortfolios.findIndex(
          (item) => item.portInfo.id === nextPortfolio.portInfo.id
        );

        if (portfolioIndex === -1) {
          return [...prevPortfolios, nextPortfolio];
        }

        return prevPortfolios.map((prevPortfolio) =>
          prevPortfolio.portInfo.id === nextPortfolio.portInfo.id
            ? { ...prevPortfolio, ...nextPortfolio }
            : prevPortfolio
        );
      });

      // Add a UDST relationship if it does not already exist
      if (!isWatchlistUserUDST) {
        updateWatchlistUserUDST("ADD_WATCHLIST_USER_UDST").then(() => {
          setIsWatchlistUserUDST(true);
        });
      }
    },
    [isWatchlistUserUDST, updateSelectedPortInfo]
  );

  const updatePortfolioInfos = useCallback((newData: Array<WatchlistInfo>) => {
    setPortfolioInfos([...newData]);
  }, []);

  const updatePortfolioForm = useCallback(
    (newData: PortfolioForm | undefined) => {
      if (!newData) {
        setPortfolioForm(undefined);

        return;
      }

      setPortfolioForm({ ...newData });
    },
    []
  );

  const removePortfolio = useCallback(
    (portfolioId: number) => {
      setPortfolioInfos((prevState) => {
        const newPortInfos = prevState.filter(
          (portInfo) => portInfo.id !== portfolioId
        );

        // update selected tab before updating all tabs
        const [newTab] = newPortInfos;

        // if no watchlists, then render new portfolio view
        if (!newPortInfos.length) {
          updateSelectedPortInfo(newTab, "NEW");
        } else {
          updateSelectedPortInfo(newTab);
        }

        // update tabs to re-render tabs
        return [...newPortInfos];
      });

      setPortfolios((prevState) => {
        const newPortfolios = prevState.filter(
          (port) => port.portInfo.id !== portfolioId
        );

        // update selected portfolio before updating all portfolio
        const [newPortfolio] = newPortfolios;

        updateSelectedPortfolio(newPortfolio);

        // delete UDST relationship if all portfolios are deleted
        if (newPortfolios.length === 0 && isWatchlistUserUDST) {
          updateWatchlistUserUDST("DELETE_WATCHLIST_USER_UDST").then(() => {
            setIsWatchlistUserUDST(false);
          });
        }

        // update portfolio list
        return [...newPortfolios];
      });
    },
    [isWatchlistUserUDST]
  );

  return (
    <PortfolioPageContext.Provider
      value={{
        portfolios,
        selectedPortInfo,
        selectedPortfolio,
        portfolioForm,
        portfolioInfos,
        removePortfolio,
        upsertPortfolio,
        updateSelectedPortInfo,
        updateSelectedPortfolio,
        updatePortfolioForm,
        updatePortfolioInfos,
        showCharts,
        renderingCharts,
        portfolioType,
        toggleCharts,
        dashboardView,
        updateDashboardView: setDashboardView,
      }}
    >
      {children}
    </PortfolioPageContext.Provider>
  );
};
